import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import { apiService } from '../services/apiService';

const Analytics = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // Last 30 days
  const [selectedMetric, setSelectedMetric] = useState('amount'); // 'amount' or 'count'

  // Analytics data states
  const [statusAnalytics, setStatusAnalytics] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    topStatus: ''
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all orders (you might want to add date filtering to your API)
      const ordersResponse = await apiService.getAllOrders();
      const allOrders = Array.isArray(ordersResponse) ? ordersResponse : 
                       (ordersResponse.content || []);

      // Filter orders based on date range
      const filteredOrders = filterOrdersByDateRange(allOrders, dateRange);
      setOrders(filteredOrders);

      // Process data for analytics
      const statusData = processStatusAnalytics(filteredOrders);
      const timeData = processTimeSeriesData(filteredOrders);
      const stats = calculateSummaryStats(filteredOrders);

      setStatusAnalytics(statusData);
      setTimeSeriesData(timeData);
      setSummaryStats(stats);

    } catch (err) {
      console.error('Failed to load analytics data:', err);
      setError('Failed to load analytics data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterOrdersByDateRange = (orders, days) => {
    if (!days || days === 'all') return orders;
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(days));

    return orders.filter(order => {
      if (!order.createdAt || !Array.isArray(order.createdAt)) return false;
      const [year, month, day] = order.createdAt;
      const orderDate = new Date(year, month - 1, day);
      return orderDate >= cutoffDate;
    });
  };

  const processStatusAnalytics = (orders) => {
    const statusGroups = {};

    orders.forEach(order => {
      const status = getDisplayStatus(order.status);
      const amount = getOrderTotal(order);

      if (!statusGroups[status]) {
        statusGroups[status] = {
          status: status,
          totalAmount: 0,
          orderCount: 0,
          orders: []
        };
      }

      statusGroups[status].totalAmount += amount;
      statusGroups[status].orderCount += 1;
      statusGroups[status].orders.push(order);
    });

    return Object.values(statusGroups).sort((a, b) => b.totalAmount - a.totalAmount);
  };

  const processTimeSeriesData = (orders) => {
    const dailyData = {};

    orders.forEach(order => {
      if (!order.createdAt || !Array.isArray(order.createdAt)) return;
      
      const [year, month, day] = order.createdAt;
      const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const status = getDisplayStatus(order.status);
      const amount = getOrderTotal(order);

      if (!dailyData[dateKey]) {
        dailyData[dateKey] = {
          date: dateKey,
          displayDate: `${month}/${day}`,
          totalAmount: 0,
          totalOrders: 0
        };
      }

      dailyData[dateKey].totalAmount += amount;
      dailyData[dateKey].totalOrders += 1;
      dailyData[dateKey][status] = (dailyData[dateKey][status] || 0) + (selectedMetric === 'amount' ? amount : 1);
    });

    return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
  };

  const calculateSummaryStats = (orders) => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + getOrderTotal(order), 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Find most common status
    const statusCounts = {};
    orders.forEach(order => {
      const status = getDisplayStatus(order.status);
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const topStatus = Object.keys(statusCounts).reduce((a, b) => 
      statusCounts[a] > statusCounts[b] ? a : b, 'N/A'
    );

    return {
      totalOrders,
      totalRevenue,
      averageOrderValue,
      topStatus
    };
  };

  const getDisplayStatus = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING_PAYMENT': return 'PENDING';
      case 'PAYMENT_FAILED': return 'FAILED';
      case 'PAYMENT_SUCCESS': return 'SUCCESS';
      case 'CONFIRMED': return 'CONFIRMED';
      case 'PROCESSING': return 'PROCESSING';
      case 'CANCELLED': return 'CANCELLED';
      case 'SHIPPED': return 'SHIPPED';
      default: return 'SUCCESS';
    }
  };

  const getOrderTotal = (order) => {
    if (order.totalAmount && order.totalAmount > 0) {
      return order.totalAmount;
    }
    
    if (order.orderItems && order.orderItems.length > 0) {
      return order.orderItems.reduce((total, item) => {
        return total + (item.quantity * item.price);
      }, 0);
    }
    
    return order.price || 0;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return '#f59e0b';
      case 'PROCESSING': return '#3b82f6';
      case 'CONFIRMED': return '#10b981';
      case 'SUCCESS': return '#059669';
      case 'SHIPPED': return '#8b5cf6';
      case 'FAILED': return '#ef4444';
      case 'CANCELLED': return '#f97316';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="analytics-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${selectedMetric === 'amount' ? formatCurrency(entry.value) : formatNumber(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <h1>Order Analytics Dashboard</h1>
        <div className="analytics-controls">
          <div className="control-group">
            <label>Date Range:</label>
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="analytics-select"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div className="control-group">
            <label>Metric:</label>
            <select 
              value={selectedMetric} 
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="analytics-select"
            >
              <option value="amount">Total Amount</option>
              <option value="count">Order Count</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="analytics-error">
          <p>{error}</p>
          <button onClick={loadAnalyticsData} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-content">
            <h3>Total Orders</h3>
            <p className="summary-value">{formatNumber(summaryStats.totalOrders)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <h3>Total Revenue</h3>
            <p className="summary-value">{formatCurrency(summaryStats.totalRevenue)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📈</div>
          <div className="summary-content">
            <h3>Avg Order Value</h3>
            <p className="summary-value">{formatCurrency(summaryStats.averageOrderValue)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🎯</div>
          <div className="summary-content">
            <h3>Top Status</h3>
            <p className="summary-value">{summaryStats.topStatus}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="analytics-charts">
        {/* Status Distribution Bar Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Orders by Status</h3>
            <p>Distribution of {selectedMetric === 'amount' ? 'revenue' : 'order count'} across different order statuses</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusAnalytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis tickFormatter={selectedMetric === 'amount' ? formatCurrency : formatNumber} />
                <Tooltip 
                  formatter={(value) => [selectedMetric === 'amount' ? formatCurrency(value) : formatNumber(value), selectedMetric === 'amount' ? 'Total Amount' : 'Order Count']}
                />
                <Bar 
                  dataKey={selectedMetric === 'amount' ? 'totalAmount' : 'orderCount'} 
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution Pie Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Status Distribution</h3>
            <p>Percentage breakdown by order status</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusAnalytics}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey={selectedMetric === 'amount' ? 'totalAmount' : 'orderCount'}
                  label={({status, percent}) => `${status} (${(percent * 100).toFixed(1)}%)`}
                >
                  {statusAnalytics.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [selectedMetric === 'amount' ? formatCurrency(value) : formatNumber(value)]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

       

        {/* Detailed Status Table */}
        <div className="chart-container full-width">
          <div className="chart-header">
            <h3>Detailed Status Breakdown</h3>
            <p>Complete analytics by order status</p>
          </div>
          <div className="analytics-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Order Count</th>
                  <th>Total Amount</th>
                  <th>Average Value</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {statusAnalytics.map((data, index) => (
                  <tr key={data.status}>
                    <td>
                      <div className="status-cell">
                        <div 
                          className="status-indicator" 
                          style={{ backgroundColor: getStatusColor(data.status) }}
                        ></div>
                        {data.status}
                      </div>
                    </td>
                    <td>{formatNumber(data.orderCount)}</td>
                    <td>{formatCurrency(data.totalAmount)}</td>
                    <td>{formatCurrency(data.totalAmount / data.orderCount)}</td>
                    <td>{((data.totalAmount / summaryStats.totalRevenue) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

         {/* Time Series Chart */}
        <div className="chart-container full-width">
          <div className="chart-header">
            <h3>Orders Over Time</h3>
            <p>Daily {selectedMetric === 'amount' ? 'revenue' : 'order count'} trends by status</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="displayDate" />
                <YAxis tickFormatter={selectedMetric === 'amount' ? formatCurrency : formatNumber} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {statusAnalytics.map((statusData, index) => (
                  <Area
                    key={statusData.status}
                    type="monotone"
                    dataKey={statusData.status}
                    stackId="1"
                    stroke={getStatusColor(statusData.status)}
                    fill={getStatusColor(statusData.status)}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;